import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { readNamespaceBundle } from './bundle-loader';

export const supportedLanguages = ['en', 'uk', 'ru', 'es'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

/** Type-guard for a supported language tag — the SSOT membership check. */
export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return (
    typeof value === 'string' && (supportedLanguages as readonly string[]).includes(value)
  );
}

export const languageNames: Record<SupportedLanguage, string> = {
  en: 'English',
  uk: 'Українська',
  ru: 'Русский',
  es: 'Español',
};

// The embedded (CRM iframe) app keeps its own language storage. The host CRM's
// UI language is authoritative there, so a preference saved in the standalone
// app must not leak into the iframe (and vice versa) — both run on the same
// origin and would otherwise share the key. Embedded also disables detector
// caching: i18n/embedded-language.ts persists only EXPLICIT in-app switches, so
// the automatic CRM-language sync never turns into a sticky "user choice".
const isEmbeddedContext =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/embedded');

/**
 * The embedded key carries a version suffix because everything written under
 * the previous one is untrustworthy.
 *
 * Until the fix in this file's sibling module, the persistence listener could
 * not tell i18next's init-time `languageChanged` from a real in-app switch, so
 * it stored whatever the detector had guessed — in the iframe, the language of
 * the VIEWER'S BROWSER — as a deliberate choice. Every user who opened the app
 * before the fix has such a value, and because a stored choice outranks the CRM
 * handoff by design, they would stay stuck on the wrong language forever: the
 * fix alone only stops NEW poisoned writes.
 *
 * Retiring the key discards genuine switches too, which is the smaller harm:
 * those users re-pick once, while everyone else lands back on their portal's
 * own language — the correct default. `v1` is deleted on load so it does not
 * sit in storage indefinitely.
 */
const EMBEDDED_LANGUAGE_KEY = 'aica-language-embedded-v2';
const LEGACY_EMBEDDED_LANGUAGE_KEY = 'aica-language-embedded';

export function purgeLegacyEmbeddedLanguage(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(LEGACY_EMBEDDED_LANGUAGE_KEY);
}

if (isEmbeddedContext && typeof localStorage !== 'undefined') {
  purgeLegacyEmbeddedLanguage(localStorage);
}

export const LANGUAGE_STORAGE_KEY = isEmbeddedContext
  ? EMBEDDED_LANGUAGE_KEY
  : 'aica-language';

/**
 * Detector precedence, which differs between the two contexts.
 *
 * Standalone: `?lang=` is a human who just clicked a localized CTA on the
 * marketing site, so it outranks a saved preference.
 *
 * Embedded: `?lang=` is NOT a human choice — it is the CRM portal's own UI
 * language, appended by the backend on every single open (edge-api
 * `embeddedRedirectUrl`). Letting it outrank localStorage would silently undo
 * the user's explicit in-app switch on every reload. It still outranks
 * `navigator`, which is the whole point of forwarding it: the portal's language
 * is a far better signal than the language of whatever browser happens to be
 * displaying the portal — an English Bitrix24 opened from a Russian-locale
 * browser must render in English.
 *
 * Exported so the ordering decision can be asserted directly; `init` below is
 * its only production caller.
 */
export function buildLanguageDetectionOrder(isEmbedded: boolean): string[] {
  return isEmbedded
    ? ['localStorage', 'querystring', 'navigator', 'htmlTag']
    : ['querystring', 'localStorage', 'navigator', 'htmlTag'];
}
// Initialize i18next (async initialization, errors handled by library)
void i18n
  // Detect user language
  .use(LanguageDetector)
  // Use custom backend for lazy loading
  .use({
    type: 'backend',
    init() {},
    read: readNamespaceBundle,
  })
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    fallbackLng: 'en',
    supportedLngs: supportedLanguages,
    ns: [
      'common',
      'admin',
      'settings',
      'dashboard',
      'billing',
      'auth',
      'records',
      'analyses',
      'alerts',
      'users',
      'projects',
      'promptBuilder',
      'analysisSetup',
      'onboarding',
      'help',
      'assistant',
      'deals',
      'dealCard',
      'crmTab',
    ],
    defaultNS: 'common',
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      // Precedence and its rationale live on buildLanguageDetectionOrder above.
      // Standalone in short: `?lang=` (marketing CTA) > saved preference >
      // browser > <html lang>, mirroring UserLanguageSync's signed-in order.
      order: buildLanguageDetectionOrder(isEmbeddedContext),
      // Embedded: no auto-caching — explicit switches are persisted manually
      // (see i18n/embedded-language.ts) so the CRM-language sync stays
      // non-sticky.
      caches: isEmbeddedContext ? [] : ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY, // context-scoped key
      lookupQuerystring: 'lang', // ?lang=uk handoff from website (config.ts signInUrl/signUpUrl)
    },
  })
  .then(() => {
    // index.html ships a static <html lang>; reflect the actually-detected
    // language for a11y/SEO once i18n resolves.
    if (typeof document !== 'undefined') {
      document.documentElement.lang = i18n.language;
    }
  });

// Keep <html lang> in sync on every change (LanguageToggle, ?lang= handoff,
// profile sync) so the document never advertises a stale language.
i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
});

export default i18n;
